import { create } from 'zustand';
import { getBackendApiClient, hasAccessToken } from '@features/backend-api';
import { getMeetingSocket } from '@features/room/container';
import type { BackendParticipant, ParticipantRole, MeetingRoomEvent } from '@shared/types';
import { useRoomStore } from '../room/roomStore';

interface ModerationState {
    meetingId: string | null;
    myParticipantId: string | null;
    myRole: ParticipantRole | null;
    participants: BackendParticipant[];
    isRoomLocked: boolean;
    isLoading: boolean;
    error: string | null;

    init: (meetingId: string, myParticipantId: string) => Promise<void>;
    reset: () => void;

    approve: (participantId: string) => Promise<void>;
    deny: (participantId: string) => Promise<void>;
    mute: (participantId: string) => Promise<void>;
    unmute: (participantId: string) => Promise<void>;
    promote: (participantId: string) => Promise<void>;
    demote: (participantId: string) => Promise<void>;
    remove: (participantId: string) => Promise<void>;
    approveToStage: (participantId: string) => Promise<void>;
    removeFromStage: (participantId: string) => Promise<void>;
    lockRoom: () => Promise<void>;
    unlockRoom: () => Promise<void>;
}

const INITIAL: Pick<ModerationState, 'meetingId' | 'myParticipantId' | 'myRole' | 'participants' | 'isRoomLocked' | 'isLoading' | 'error'> = {
    meetingId: null,
    myParticipantId: null,
    myRole: null,
    participants: [],
    isRoomLocked: false,
    isLoading: false,
    error: null,
};

function resolveAuthOverride(): string | undefined {
  if (hasAccessToken()) return undefined;
  return useRoomStore.getState().wsTicket ?? undefined;
}

export const useModerationStore = create<ModerationState>((set, get) => {
    // Единый WS-слушатель на всё время жизни стора; фильтрует по активному meetingId.
    getMeetingSocket().onEvent((event: MeetingRoomEvent) => {
        const { meetingId } = get();
        if (!meetingId) return;
        applyEvent(set, get, event);
    });

    return {
        ...INITIAL,

        init: async (meetingId, myParticipantId) => {
            set({ meetingId, myParticipantId, isLoading: true, error: null });
            try {
                const participants = await getBackendApiClient().listParticipants(meetingId);
                const me = participants.find((p) => p.id === myParticipantId);
                set({ participants, myRole: me?.role ?? null, isLoading: false });
            } catch (err) {
                set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load participants' });
            }
        },

        reset: () => set({ ...INITIAL }),

        approve: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().approveParticipant(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to approve participant' });
            }
        },
        deny: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().denyParticipant(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to deny participant' });
            }
        },
        mute: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().muteParticipant(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to mute participant' });
            }
        },
        unmute: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().unmuteParticipant(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to unmute participant' });
            }
        },
        promote: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().promoteParticipant(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to promote participant' });
            }
        },
        demote: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().demoteParticipant(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to demote participant' });
            }
        },
        remove: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().removeParticipant(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to remove participant' });
            }
        },
        approveToStage: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().approveParticipantToStage(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to approve participant to stage' });
            }
        },
        removeFromStage: async (participantId) => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().removeParticipantFromStage(meetingId, participantId, resolveAuthOverride());
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to remove participant from stage' });
            }
        },
        lockRoom: async () => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().lockRoom(meetingId, resolveAuthOverride());
                set({ isRoomLocked: true });
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to lock room' });
            }
        },
        unlockRoom: async () => {
            const { meetingId } = get();
            if (!meetingId) return;
            try {
                await getBackendApiClient().unlockRoom(meetingId, resolveAuthOverride());
                set({ isRoomLocked: false });
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to unlock room' });
            }
        },
    };
});

// ─── WS event application ──────────────────────────────────────────────────

function applyEvent(
    set: (patch: Partial<ModerationState>) => void,
    get: () => ModerationState,
    event: MeetingRoomEvent,
): void {
    const { participants, myParticipantId } = get();

    switch (event.type) {
        case 'ParticipantJoined': {
            const exists = participants.some((p) => p.id === event.participantId);
            if (exists) return;
            const meetingId = get().meetingId;
            if (!meetingId) return;
            void getBackendApiClient()
                .listParticipants(meetingId)
                .then((fresh) => set({ participants: fresh }))
                .catch(() => {
                });
            return;
        }

        case 'ParticipantLeft': {
            set({
                participants: participants.filter((p) => p.id !== event.participantId),
            });
            return;
        }

        case 'ParticipantApproved': {
            set({
                participants: participants.map((p) =>
                    p.id === event.participantId ? { ...p, status: 'Approved' } : p,
                ),
            });
            return;
        }

        case 'ParticipantDenied': {
            set({
                participants: participants.map((p) =>
                    p.id === event.participantId ? { ...p, status: 'Denied' } : p,
                ),
            });
            return;
        }

        case 'HostAction': {
            const targetId = event.targetId;

            if (event.action === 'lock_room') {
                set({ isRoomLocked: true });
                return;
            }
            if (event.action === 'unlock_room') {
                set({ isRoomLocked: false });
                return;
            }
            if (!targetId) return;

            if (event.action === 'remove_participant') {
                set({ participants: participants.filter((p) => p.id !== targetId) });
                return;
            }
            if (event.action === 'promote_moderator') {
                set({ participants: participants.map((p) => (p.id === targetId ? { ...p, role: 'Moderator' } : p)) });
                if (targetId === myParticipantId) {
                    set({ myRole: 'Moderator' });
                    useRoomStore.getState().refreshRoomCredentials();
                }
                return;
            }
            if (event.action === 'demote_moderator') {
                set({ participants: participants.map((p) => (p.id === targetId ? { ...p, role: 'Viewer' } : p)) });
                if (targetId === myParticipantId) {
                    set({ myRole: 'Viewer' });
                    useRoomStore.getState().refreshRoomCredentials();
                }
                return;
            }
            if (event.action === 'approve_to_stage') {
                set({ participants: participants.map((p) => (p.id === targetId ? { ...p, role: 'OnCameraGuest' } : p)) });
                return;
            }
            if (event.action === 'remove_from_stage') {
                set({ participants: participants.map((p) => (p.id === targetId ? { ...p, role: 'Viewer' } : p)) });
                return;
            }
            // mute/unmute — не меняют локальный список участников (это LiveKit-уровень);
            // сам факт мьюта уже виден через RoomControls/participants в roomStore при желании.
            return;
        }

        case 'MeetingStatusChanged':
        case 'MediaTokenIssued':
            return; // обрабатываются в других сторах (roomStore)
    }
}